import { useRef, useMemo } from 'react';
import { useTable, useFilters, useSortBy, useFlexLayout } from 'react-table';
import { array, bool, number, element, string, shape } from 'prop-types';
import { TextStyled, ButtonStyled, useTokens } from '@nike/nike-design-system-components';

import useWindowSize from '../../../hooks/useWindowSize';
import { scrollbarWidth, filterYesNoFromBoolean } from './helpers';

import { TableStyled, StickyButton } from './styledTableElements';
import { Icon } from '../../index';
import { TranslatedCopy } from '../../../intl/TranslatedCopy';

/*
 * React table with sorting and filtering capabilties.
 * All table data is fetched initially in parent component and passed to TableClientSide.
 */
const TableClientSide = ({
  columns,
  data, // All fetched table data, any updates to it are made in parent component and passed
  defaultSort, // { id, desc }
  rightHeaderSlot, // Optional element or component rendering on upper right above table
  fullHeight,
  fullWidth,
  height
}) => {
  const tokens = useTokens();

  // Custom filter functions
  const filterTypes = useMemo(
    () => ({
      yesNo: filterYesNoFromBoolean
    }),
    []
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, setSortBy, setAllFilters } = useTable(
    { columns, data, filterTypes, ...(defaultSort ? { initialState: { sortBy: [defaultSort] } } : {}) },
    useFlexLayout,
    useFilters,
    useSortBy
  );

  const tableRef = useRef(null);
  const tableBodyRef = useRef(null);

  const { width } = useWindowSize();
  const scrollbarSize = useMemo(scrollbarWidth, [width]);
  const heightToBottomOfWindow = window.innerHeight - (tableRef?.current?.offsetTop + 55 + scrollbarSize);

  const scrollToTop = () => tableBodyRef.current?.scrollTo?.({ left: 0, top: 0, behavior: 'auto' });

  const handleClearSortAndFilters = () => {
    setSortBy([]);
    setAllFilters([]);
  };

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '5px',
          height: '50px',
          alignContent: 'center'
        }}
      >
        <ButtonStyled
          appearance='text'
          background='light'
          ariaLabel='Clear All Filters'
          type='button'
          onClick={handleClearSortAndFilters}
          style={{ margin: '0 10px' }}
        >
          <TranslatedCopy id='gridwall.filters.clear' />
        </ButtonStyled>

        <div style={{ display: 'flex', alignContent: 'center' }}>{rightHeaderSlot}</div>
      </div>
      <div>
        <TableStyled
          {...getTableProps()}
          ref={tableRef}
          designTokens={tokens}
          fullWidth={fullWidth}
          scrollbarSize={scrollbarSize}
        >
          <div>
            {headerGroups.map(headerGroup => (
              <div {...headerGroup.getHeaderGroupProps()} className='tr'>
                {headerGroup.headers.map(column => (
                  <div {...column.getHeaderProps()} className='th'>
                    {column.render('Header')}
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div
            {...getTableBodyProps()}
            className='tbody'
            ref={tableBodyRef}
            style={{ height: `${fullHeight ? heightToBottomOfWindow : height}px` }}
          >
            {
              // eslint-disable-next-line no-nested-ternary
              rows.length === 0 ? (
                <div style={{ position: 'absolute', padding: '20px' }}>
                  <TextStyled appearance='body1'>
                    <TranslatedCopy id='global.no.values' />.
                  </TextStyled>
                </div>
              ) : (
                rows.map(row => {
                  prepareRow(row);
                  return (
                    <div {...row.getRowProps()} className='tr'>
                      {row.cells.map(cell => (
                        <div {...cell.getCellProps()} className='td'>
                          {cell.render('Cell')}
                        </div>
                      ))}
                    </div>
                  );
                })
              )
            }
          </div>
        </TableStyled>
      </div>

      <StickyButton type='button' onClick={scrollToTop}>
        <Icon name='arrow-up' isLight alt='Arrow Up Icon' />
      </StickyButton>
    </>
  );
};

TableClientSide.propTypes = {
  columns: array.isRequired,
  data: array,
  rightHeaderSlot: element,
  fullHeight: bool,
  fullWidth: bool,
  height: number,
  defaultSort: shape({
    id: string.isRequired,
    desc: bool.isRequired
  })
};

TableClientSide.defaultProps = {
  data: [],
  rightHeaderSlot: null,
  fullHeight: false,
  fullWidth: false,
  height: 500,
  defaultSort: null
};

export default TableClientSide;
